import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchFeatureFlag, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A14.02 — Flag kartasi. GET /admin/settings/flags dan kalit bo'yicha olinadi.
 * O'zgartirish (A14.03) = mutatsiya-endpoint + tasdiq bilan follow-up.
 */
export function FeatureFlagCard() {
  const t = useT()
  const { can } = useAuth()
  const { key = '' } = useParams()
  const flagKey = decodeURIComponent(key)
  const [flag, setFlag] = useState<AdminFeatureFlag | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchFeatureFlag(flagKey, ac.signal)
      .then((f) => {
        if (alive) setFlag(f)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [flagKey, reload])

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={flag ? flag.key : t.flags.card}
        screenId="A14.02"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !flag ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3">
              <StatusBadge status={flag.enabled ? 'active' : 'deleted'} />
            </div>
            <Field label={t.flags.key}>
              <MonoValue value={flag.key} />
            </Field>
            <Field label={t.flags.value}>
              <MonoValue value={flag.value} />
            </Field>
            <Field label={t.col.status}>{flag.enabled ? t.flags.on : t.flags.off}</Field>
            <Field label={t.finance.updated}>
              <RelativeTime value={flag.updated_at} />
            </Field>
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
